/**
 * 「这一张排不下」的提示(2026-07-30)。
 *
 * ═══ 两次改口径,记在这里 ═══════════════════════════════════════════════════
 * ① 一开始:页面上常驻一个「组图模式 · 按爆点分张」按钮。
 *    错在**问了一个用户此刻答不上来的问题**(我要不要分张?)——答案取决于他还没写完
 *    的内容;而且所有人(包括只想出一张的)都得先面对这个选择。已删。
 *
 * ② 然后:改成"内容超载时建议拆成 N 张,每张讲一个"。
 *    ★这一版**在推销一个我们刚承认只换文字的能力** —— 组图现在每张只换文案、
 *      风格构图同一套(用户诊断的原话:「基本就是只换了文字」)。
 *      让用户为此多付 5 份钱,建议本身就不成立。
 *
 * ③ 我改成了"让 AI 挑重点、少放几条"。**也错**:用户不想删卖点 ——
 *    他 20 条卖点条条都要,问题不是"哪几条重要",是"一张纸排不下"。
 *
 * ④ 现在(用户定的):**还是推荐去组图,但拆几张按"页面排得下多少"算**。
 *    一张海报排得下约 6 条 → 20 条 ≈ 4 张。不是"每张讲一个"(20 条拆 20 张太碎、
 *    也在推销那个只换文字的形态),也不是"删到 6 条"(卖点是用户的,我们无权替他删)。
 *    张数由**版面容量**推出来,所以这条建议站得住:它陈述的是排布事实。
 *
 * ★三次改口径的共同教训:提示的**依据**必须是我们真判得出来的东西(版面装得下多少),
 *   而不是我们希望用户做的动作(去用组图 / 少写点)。依据对了,动作才推得动。
 */
import type { FreeCreateProduct } from "./productAdapter";

/** 一张海报排得下的卖点条数(超过就该建议分张)。 */
export const POINTS_PER_IMAGE = 6;

/** 组图张数上限(与 `MAX_GROUP_POINTS` 同值)。 */
export const MAX_GROUP_IMAGES = 6;

export interface OverloadHint {
  /** 识别到的条目数。 */
  count: number;
  /**
   * 建议分几张 —— **按版面容量算**:⌈条目数 / 一张排得下的条数⌉。
   * ★不是"每张讲一个":20 条拆 20 张太碎,而且组图上限就是 6 张。
   * ★封顶 `MAX_GROUP_IMAGES`;真超了就是"先出这么多",而不是假装排得下。
   */
  suggest: number;
  /** 这些条目从哪来 —— 措辞要说清楚,否则用户不知道我们在数什么。 */
  from: "product" | "text";
}

/**
 * 从用户文案里数「分点」—— 只认**明确的分点写法**,不做语义切分。
 *
 * ★宁可漏判不可错判:把一段连贯描述误切成 8 条,然后建议他出 6 张图,
 *   比不建议糟得多(他会照做,然后拿到 6 张莫名其妙的图)。
 * ★所以只认三类硬信号:换行分点、序号(1. / 1、)、顿号分隔的长列表。
 */
export function countTextPoints(text: string): number {
  const t = (text || "").trim();
  if (!t) return 0;
  const lines = t
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 1);
  // ① 多行 + 过半行以「序号 / 符号」开头 = 明确的分点写法
  const bulletish = lines.filter((l) =>
    /^([0-9]{1,2}[.、)]|[-·•*]|第[一二三四五六七八九十]+[、.])/.test(l)
  );
  if (lines.length >= 3 && bulletish.length >= Math.ceil(lines.length / 2)) return lines.length;
  // ② 单行里顿号分隔的长列表(「A、B、C、D、E、F」)
  const dun = t
    .split(/[、;;]/)
    .map((s) => s.trim())
    .filter((s) => s.length >= 2);
  if (dun.length >= POINTS_PER_IMAGE + 1) return dun.length;
  return 0;
}

/**
 * 这一单要不要建议拆图。返回 null = 不建议(装得下 / 已经是组图 / 数不清)。
 *
 * @param groupModeActive 已经在组图态就不再建议(他已经在做这件事了)
 */
export function detectOverload(input: {
  product: FreeCreateProduct | null;
  text: string;
  groupModeActive: boolean;
  isI2I: boolean;
}): OverloadHint | null {
  if (input.groupModeActive || input.isI2I) return null;

  // 产品卖点优先:它是**结构化**的,数得准;文案分点是兜底。
  const fromProduct = input.product?.sellingPoints?.length ?? 0;
  const count = fromProduct > POINTS_PER_IMAGE ? fromProduct : countTextPoints(input.text);
  if (count <= POINTS_PER_IMAGE) return null;

  return {
    count,
    // ★张数由**版面容量**推出:⌈20 / 6⌉ = 4 张,不是 20 张也不是固定 6 张。
    suggest: Math.min(Math.ceil(count / POINTS_PER_IMAGE), MAX_GROUP_IMAGES),
    from: fromProduct > POINTS_PER_IMAGE ? "product" : "text",
  };
}
